import { useState, useMemo } from 'react';
import { ArrowLeft, BarChart3, Eye, MousePointer, Clock, TrendingUp, Monitor, Globe, MousePointerClick, Activity, Target, Zap, CheckCircle2, ArrowUpRight, ArrowDownRight, ExternalLink, Shield, Code } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { useChartTokens } from '@/hooks/useChartTokens';
import { TRACKING_SERVICES, detectActiveTrackingServices } from './tracking-config';
import type { LandingAnalyticsDto } from '@easyflow/shared';

// Le righe aggregate provengono da query SQL dinamiche (jsonb / colonne
// variabili): ogni riga è un record largo. `pInt` coercisce i valori wire
// (numeric pg → stringa, o unknown da jsonb) a intero in modo difensivo.
type AnalyticsRow = Record<string, unknown>;
const pInt = (v: unknown): number => {
  const n = Number.parseInt(String(v ?? ''), 10);
  return Number.isFinite(n) ? n : 0;
};
const asStr = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
// webVitals arriva come record jsonb: coercisce a number|null (pg numeric =
// stringa sul wire → parse difensivo).
const asNum = (v: unknown): number | null => {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number.parseFloat(String(v));
  return Number.isFinite(n) ? n : null;
};

interface LandingAnalyticsDashboardProps {
  analyticsData: LandingAnalyticsDto | null;
  pages: AnalyticsRow[];
  mode: 'page' | 'project';
  pageTitle?: string;
  onBack: () => void;
  onPageDrillDown?: (pageId: string) => void;
  customHeadCode?: string;
  onPeriodChange?: (days: number) => void;
  onNavigateToCode?: () => void;
}

const TABS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'traffic', label: 'Traffico', icon: Globe },
  { id: 'behavior', label: 'Comportamento', icon: Activity },
  { id: 'conversions', label: 'Conversioni', icon: Target },
  { id: 'tracking', label: 'Tracking', icon: Shield },
  { id: 'webvitals', label: 'Web Vitals', icon: Zap },
] as const;

type TabId = typeof TABS[number]['id'];

const PERIOD_OPTIONS = [
  { value: 7, label: '7 giorni' },
  { value: 30, label: '30 giorni' },
  { value: 90, label: '90 giorni' },
];

const DAYS = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];

export default function LandingAnalyticsDashboard({
  analyticsData,
  pages,
  mode,
  pageTitle,
  onBack,
  onPageDrillDown,
  customHeadCode = '',
  onPeriodChange,
  onNavigateToCode,
}: LandingAnalyticsDashboardProps) {
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [period, setPeriod] = useState(30);
  const tokens = useChartTokens();
  const tooltipStyle = { fontSize: 12, borderRadius: 8, background: tokens.cardBg, border: `1px solid ${tokens.border}`, color: tokens.fg } as const;
  const COLORS_DYN = [tokens.google, tokens.elevenlabs, tokens.claude, tokens.openai, '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];

  const {
    totals, last7, topReferrers, devices, eventCounts, avgTimeOnPage, bounceRate,
    webVitals, clickData, daily, utmBreakdown, scrollDepthDist, heatmapData,
    ctaConversionRate, pageBreakdown, currentWeekConversions, prevWeekConversions
  } = analyticsData ?? {};

  const activeTracking = useMemo(() => detectActiveTrackingServices(customHeadCode), [customHeadCode]);

  const filteredDaily = useMemo(() => {
    if (!daily || daily.length === 0) return [];
    return daily.slice(-period);
  }, [daily, period]);

  const dailyChartData = useMemo(() => {
    return filteredDaily.map((d: AnalyticsRow) => ({
      date: d.date ? new Date(asStr(d.date)).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }) : '',
      visite: pInt(d.views),
      unici: pInt(d.unique_views),
    }));
  }, [filteredDaily]);

  const deviceChartData = useMemo(() => {
    if (!devices || devices.length === 0) return [];
    return devices.map((d: AnalyticsRow) => ({
      name: asStr(d.device_type) || 'desktop',
      value: pInt(d.views ?? d.count),
    }));
  }, [devices]);

  const referrerChartData = useMemo(() => {
    if (!topReferrers || topReferrers.length === 0) return [];
    return topReferrers.slice(0, 8).map((r: AnalyticsRow) => ({
      name: (asStr(r.referrer) || 'Diretto').substring(0, 30),
      visite: pInt(r.views ?? r.count),
    }));
  }, [topReferrers]);

  const utmChartData = useMemo(() => {
    if (!utmBreakdown || utmBreakdown.length === 0) return [];
    return utmBreakdown.slice(0, 10).map((u: AnalyticsRow) => ({
      name: [u.utm_source, u.utm_medium, u.utm_campaign].filter(Boolean).join(' / ') || '-',
      visite: pInt(u.views),
    }));
  }, [utmBreakdown]);

  const scrollChartData = useMemo(() => {
    if (!scrollDepthDist || scrollDepthDist.length === 0) return [];
    const depths = [25, 50, 75, 100];
    return depths.map(d => {
      const match = scrollDepthDist.find((s: AnalyticsRow) => pInt(s.depth) === d);
      return { depth: `${d}%`, count: pInt(match?.count) };
    });
  }, [scrollDepthDist]);

  const heatmapGrid = useMemo(() => {
    if (!heatmapData || heatmapData.length === 0) return null;
    const grid: number[][] = Array.from({ length: 7 }, () => Array(24).fill(0));
    let maxVal = 0;
    heatmapData.forEach((h: AnalyticsRow) => {
      const day = pInt(h.day_of_week);
      const hour = pInt(h.hour_of_day);
      const count = pInt(h.count);
      if (day >= 0 && day < 7 && hour >= 0 && hour < 24) {
        grid[day]![hour] = count;
        if (count > maxVal) maxVal = count;
      }
    });
    return { grid, maxVal };
  }, [heatmapData]);

  const totalViews = pInt(totals?.total_views) || pInt(totals?.total_unique_views);
  const uniqueVisitors = pInt(totals?.unique_visitors) || pInt(totals?.total_unique_views);

  const getWebVitalStatus = (metric: string, value: number | null | undefined): 'good' | 'warning' | 'bad' | 'none' => {
    if (value === null || value === undefined) return 'none';
    const thresholds: Record<string, [number, number]> = { lcp: [2500, 4000], cls: [0.1, 0.25], inp: [200, 500] };
    const t = thresholds[metric];
    if (!t) return 'none';
    return value < t[0] ? 'good' : value < t[1] ? 'warning' : 'bad';
  };

  const getOverallWebVitalStatus = (): 'good' | 'warning' | 'bad' | 'none' => {
    if (!webVitals) return 'none';
    const statuses = [
      getWebVitalStatus('lcp', asNum(webVitals.lcp)),
      getWebVitalStatus('cls', asNum(webVitals.cls)),
      getWebVitalStatus('inp', asNum(webVitals.inp)),
    ].filter(s => s !== 'none');
    if (statuses.length === 0) return 'none';
    if (statuses.includes('bad')) return 'bad';
    if (statuses.includes('warning')) return 'warning';
    return 'good';
  };

  const ctaClicks = pInt(eventCounts?.find((e: AnalyticsRow) => e.event_type === 'cta_click')?.count);
  const formSubmits = pInt(eventCounts?.find((e: AnalyticsRow) => e.event_type === 'form_click')?.count);

  if (!analyticsData) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <BarChart3 className="h-12 w-12 mb-3" />
        <p className="text-sm">Nessun dato disponibile</p>
        <Button variant="ghost" size="sm" onClick={onBack} className="mt-4"><ArrowLeft className="h-4 w-4 mr-1" /> Indietro</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" /> Indietro</Button>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-sky-500" />
            {mode === 'project' ? 'Analytics Progetto' : `Analytics: ${pageTitle || 'Pagina'}`}
          </h3>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
          {PERIOD_OPTIONS.map(opt => (
            <button
              key={opt.value}
              onClick={() => { setPeriod(opt.value); onPeriodChange?.(opt.value); }}
              className={`px-3 py-1 text-xs rounded-md transition-colors ${period === opt.value ? 'bg-card shadow text-sky-600 font-medium' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-border">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-sky-500 text-sky-600 dark:text-sky-400'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {tab.label}
              {tab.id === 'tracking' && activeTracking.length > 0 && (
                <span className="ml-1 w-4 h-4 rounded-full bg-green-500 text-white text-[9px] flex items-center justify-center font-bold">{activeTracking.length}</span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard icon={<Eye className="h-4 w-4 text-blue-500" />} label="Visite Totali" value={totalViews} sub={last7 ? `${pInt(last7.views_7d)} ultimi 7gg` : undefined} />
            <StatCard icon={<MousePointer className="h-4 w-4 text-green-500" />} label="Visitatori Unici" value={uniqueVisitors} sub={last7 ? `${pInt(last7.unique_7d)} ultimi 7gg` : undefined} />
            <StatCard icon={<Clock className="h-4 w-4 text-amber-500" />} label="Tempo Medio" value={avgTimeOnPage ? `${Math.round(avgTimeOnPage)}s` : '-'} />
            <StatCard icon={<TrendingUp className="h-4 w-4 text-purple-500" />} label="Bounce Rate" value={bounceRate ? `${Math.round(bounceRate)}%` : '-'} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {topReferrers && topReferrers.length > 0 && (
              <div className="bg-card rounded-lg border border-border p-4">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5" /> Top Referrer
                </h4>
                <div className="space-y-2">
                  {topReferrers.slice(0, 3).map((r: AnalyticsRow, i: number) => {
                    const name = (asStr(r.referrer) || 'Diretto').substring(0, 25);
                    const count = pInt(r.views ?? r.count);
                    return (
                      <div key={i} className="flex items-center justify-between">
                        <span className="text-xs text-foreground truncate">{name}</span>
                        <span className="text-xs font-medium text-sky-600 ml-2">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Target className="h-3.5 w-3.5" /> Conversioni Rapide
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground">Click CTA</span>
                  <span className="text-xs font-medium text-orange-600">{ctaClicks}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground">Form Submit</span>
                  <span className="text-xs font-medium text-green-600">{formSubmits}</span>
                </div>
                {ctaConversionRate != null && ctaConversionRate > 0 && (
                  <div className="flex items-center justify-between pt-1 border-t border-border">
                    <span className="text-xs text-foreground">Conv. Rate</span>
                    <span className="text-xs font-bold text-sky-600">{ctaConversionRate}%</span>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Zap className="h-3.5 w-3.5" /> Web Vitals
              </h4>
              {(() => {
                const overall = getOverallWebVitalStatus();
                const statusConfig = {
                  good: { color: 'text-green-600', bg: 'bg-green-100 dark:bg-green-900/30', dot: 'bg-green-500', label: 'Buono' },
                  warning: { color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-900/30', dot: 'bg-amber-500', label: 'Da migliorare' },
                  bad: { color: 'text-red-600', bg: 'bg-red-100 dark:bg-red-900/30', dot: 'bg-red-500', label: 'Scarso' },
                  none: { color: 'text-muted-foreground', bg: 'bg-muted', dot: 'bg-muted-foreground/40', label: 'Nessun dato' },
                };
                const cfg = statusConfig[overall];
                return (
                  <div className="space-y-2">
                    <div className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${cfg.bg}`}>
                      <div className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                      <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>
                    </div>
                    {webVitals && (
                      <div className="grid grid-cols-3 gap-1 text-center">
                        {(['lcp', 'cls', 'inp'] as const).map(metric => {
                          const val = asNum(webVitals[metric]);
                          const st = getWebVitalStatus(metric, val);
                          const dotColor = { good: 'bg-green-500', warning: 'bg-amber-500', bad: 'bg-red-500', none: 'bg-muted-foreground/40' }[st];
                          return (
                            <div key={metric} className="flex flex-col items-center">
                              <div className={`w-1.5 h-1.5 rounded-full ${dotColor} mb-0.5`} />
                              <span className="text-[10px] text-muted-foreground uppercase">{metric}</span>
                              <span className="text-[10px] font-medium text-foreground">{val !== null ? (metric === 'cls' ? val : `${val}ms`) : '-'}</span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {activeTracking.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <Shield className="h-3.5 w-3.5" /> Tracking Attivi
              </h4>
              <div className="flex flex-wrap gap-2">
                {activeTracking.map(t => (
                  <a
                    key={t.serviceId}
                    href={t.service.dashboardUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors"
                  >
                    <span>{t.service.icon}</span>
                    {t.service.label}
                    <ExternalLink className="h-2.5 w-2.5" />
                  </a>
                ))}
              </div>
            </div>
          )}

          {dailyChartData.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">Visite nel Tempo</h4>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={dailyChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={tokens.grid} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="visite" stroke={tokens.google} strokeWidth={2} dot={false} name="Visite" />
                  <Line type="monotone" dataKey="unici" stroke={tokens.elevenlabs} strokeWidth={2} dot={false} name="Unici" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {heatmapGrid && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">Heatmap Visite (Ore / Giorni)</h4>
              <div className="overflow-x-auto">
                <div className="min-w-[600px]">
                  <div className="flex items-center mb-1">
                    <div className="w-10" />
                    {Array.from({ length: 24 }, (_, i) => (
                      <div key={i} className="flex-1 text-center text-[9px] text-muted-foreground">{i}</div>
                    ))}
                  </div>
                  {DAYS.map((day, dayIdx) => (
                    <div key={dayIdx} className="flex items-center gap-0">
                      <div className="w-10 text-[10px] text-muted-foreground font-medium">{day}</div>
                      {Array.from({ length: 24 }, (_, hourIdx) => {
                        const val = heatmapGrid.grid[dayIdx]![hourIdx]!;
                        const intensity = heatmapGrid.maxVal > 0 ? val! / heatmapGrid.maxVal : 0;
                        return (
                          <div
                            key={hourIdx}
                            className="flex-1 aspect-square rounded-sm mx-px"
                            style={{
                              backgroundColor: intensity > 0
                                ? `rgba(14, 165, 233, ${Math.max(0.1, intensity)})`
                                : 'rgba(0,0,0,0.03)',
                            }}
                            title={`${day} ${hourIdx}:00 - ${val} visite`}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {mode === 'project' && onPageDrillDown && pages.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">Pagine del Progetto</h4>
              <div className="space-y-1">
                {(pageBreakdown || pages).map((p: AnalyticsRow) => {
                  const pageId = asStr(p.id) || asStr(p.page_id);
                  return (
                  <button key={pageId} onClick={() => onPageDrillDown(pageId)} className="w-full flex items-center justify-between p-2 rounded hover:bg-muted/50 text-xs transition-colors">
                    <span className="text-foreground font-medium">{asStr(p.title) || 'Pagina'}</span>
                    <div className="flex items-center gap-3">
                      {p.views ? <span className="text-muted-foreground">{asStr(p.views)} visite</span> : null}
                      {p.bounce_rate !== undefined && <span className="text-muted-foreground">BR: {asStr(p.bounce_rate)}%</span>}
                      <span className="text-sky-500">Dettagli →</span>
                    </div>
                  </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'traffic' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {deviceChartData.length > 0 && (
              <div className="bg-card rounded-lg border border-border p-4">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Monitor className="h-4 w-4" /> Dispositivi
                </h4>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={deviceChartData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3}>
                      {deviceChartData.map((_, idx: number) => (
                        <Cell key={idx} fill={COLORS_DYN[idx % COLORS_DYN.length]} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {referrerChartData.length > 0 && (
              <div className="bg-card rounded-lg border border-border p-4">
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Globe className="h-4 w-4" /> Top Referrer
                </h4>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={referrerChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={tokens.grid} />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={100} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar dataKey="visite" fill={tokens.google} radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {utmChartData.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">UTM Breakdown (Source / Medium / Campaign)</h4>
              <ResponsiveContainer width="100%" height={Math.max(180, utmChartData.length * 28)}>
                <BarChart data={utmChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke={tokens.grid} />
                  <XAxis type="number" tick={{ fontSize: 10 }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={160} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="visite" fill={tokens.elevenlabs} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {(!deviceChartData.length && !referrerChartData.length && !utmChartData.length) && (
            <EmptyState message="Nessun dato di traffico disponibile per il periodo selezionato" />
          )}
        </div>
      )}

      {activeTab === 'behavior' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatCard icon={<Clock className="h-4 w-4 text-amber-500" />} label="Tempo Medio Permanenza" value={avgTimeOnPage ? `${Math.round(avgTimeOnPage)}s` : '-'} />
            <StatCard icon={<TrendingUp className="h-4 w-4 text-purple-500" />} label="Bounce Rate" value={bounceRate ? `${Math.round(bounceRate)}%` : '-'} />
            <StatCard icon={<Activity className="h-4 w-4 text-sky-500" />} label="Scroll Medio" value={scrollDepthDist && scrollDepthDist.length > 0 ? (asStr(scrollDepthDist[scrollDepthDist.length - 1]?.depth) || '-') : '-'} />
          </div>

          {scrollChartData.length > 0 && scrollChartData.some(s => s.count > 0) && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">Scroll Depth Distribution</h4>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={scrollChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke={tokens.grid} />
                  <XAxis dataKey="depth" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Area type="monotone" dataKey="count" stroke={tokens.elevenlabs} fill={tokens.elevenlabs} fillOpacity={0.2} strokeWidth={2} name="Sessioni" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {eventCounts && eventCounts.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">Eventi Tracciati</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {eventCounts.map((e: AnalyticsRow, i: number) => (
                  <div key={i} className="text-center p-2 rounded-lg bg-muted/50">
                    <p className="text-lg font-bold text-foreground">{asStr(e.count)}</p>
                    <p className="text-xs text-muted-foreground">{formatEventType(asStr(e.event_type))}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {mode === 'project' && pageBreakdown && pageBreakdown.length > 1 && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">Percorsi tra Pagine</h4>
              <div className="space-y-2">
                {pageBreakdown.map((p: AnalyticsRow, i: number) => {
                  const maxViews = Math.max(...pageBreakdown.map((pp: AnalyticsRow) => pInt(pp.views) || 1));
                  const pct = Math.round((pInt(p.views) / maxViews) * 100);
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-medium text-foreground truncate">{asStr(p.title) || asStr(p.slug)}</span>
                          <span className="text-xs text-muted-foreground ml-2">{asStr(p.views)} visite</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-2">
                          <div className="bg-gradient-to-r from-sky-400 to-sky-600 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(!scrollChartData.some(s => s.count > 0) && (!eventCounts || eventCounts.length === 0)) && (
            <EmptyState message="Nessun dato comportamentale disponibile" />
          )}
        </div>
      )}

      {activeTab === 'conversions' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatCard
              icon={<Target className="h-4 w-4 text-rose-500" />}
              label="Conversione CTA"
              value={ctaConversionRate !== undefined ? `${ctaConversionRate}%` : '-'}
            />
            <StatCard
              icon={<MousePointerClick className="h-4 w-4 text-orange-500" />}
              label="Click CTA"
              value={ctaClicks}
            />
            <StatCard
              icon={<Eye className="h-4 w-4 text-blue-500" />}
              label="Form Submit"
              value={formSubmits}
            />
            <StatCard
              icon={<Activity className="h-4 w-4 text-green-500" />}
              label="Sessioni Totali"
              value={pInt(eventCounts?.find((e: AnalyticsRow) => e.event_type === 'session_start')?.count)}
            />
          </div>

          {clickData && clickData.length > 0 && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <MousePointerClick className="h-4 w-4 text-rose-500" /> Click per CTA
              </h4>
              <div className="space-y-2">
                {clickData.map((c: AnalyticsRow, i: number) => {
                  const maxClicks = Math.max(...clickData.map((cc: AnalyticsRow) => pInt(cc.count) || 1));
                  const pct = Math.round((pInt(c.count) / maxClicks) * 100);
                  return (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-foreground truncate flex-1">{asStr(c.element_text) || asStr(c.element_id) || 'CTA'}</span>
                        <span className="text-xs font-medium text-muted-foreground ml-2">{asStr(c.count)} click</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-1.5">
                        <div className="bg-gradient-to-r from-rose-400 to-rose-600 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {ctaConversionRate != null && ctaConversionRate > 0 && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">Conversione Funnel</h4>
              <div className="flex items-center gap-2">
                <FunnelStep label="Visite" value={totalViews} color="bg-sky-500" />
                <ArrowRight />
                <FunnelStep label="CTA Click" value={ctaClicks} color="bg-amber-500" />
                <ArrowRight />
                <FunnelStep label="Form" value={formSubmits} color="bg-green-500" />
              </div>
            </div>
          )}

          {currentWeekConversions && prevWeekConversions && (pInt(currentWeekConversions.sessions) > 0 || pInt(prevWeekConversions.sessions) > 0) && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">Confronto Settimana Corrente vs Precedente</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <ComparisonCard label="Click CTA" current={pInt(currentWeekConversions.ctaClicks)} previous={pInt(prevWeekConversions.ctaClicks)} />
                <ComparisonCard label="Form Submit" current={pInt(currentWeekConversions.formClicks)} previous={pInt(prevWeekConversions.formClicks)} />
                <ComparisonCard label="Sessioni" current={pInt(currentWeekConversions.sessions)} previous={pInt(prevWeekConversions.sessions)} />
                <ComparisonCard label="Conv. Rate" current={asNum(currentWeekConversions.ctaConversionRate) ?? 0} previous={asNum(prevWeekConversions.ctaConversionRate) ?? 0} suffix="%" />
              </div>
            </div>
          )}

          {(!clickData || clickData.length === 0) && !ctaConversionRate && (
            <EmptyState message="Nessun dato di conversione disponibile" />
          )}
        </div>
      )}

      {activeTab === 'tracking' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Shield className="h-4 w-4 text-sky-500" />
                Stato Servizi di Tracking
              </h4>
              <p className="text-xs text-muted-foreground mt-0.5">Riepilogo dei servizi di tracking configurati. Per aggiungere, modificare o rimuovere servizi, vai alla sezione Codice.</p>
            </div>
            {activeTracking.length > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                {activeTracking.length} attiv{activeTracking.length === 1 ? 'o' : 'i'}
              </span>
            )}
          </div>

          {activeTracking.length > 0 ? (
            <div className="space-y-3">
              {activeTracking.map(t => (
                <div key={t.serviceId} className="bg-card rounded-lg border border-green-200 dark:border-green-800 p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0" style={{ backgroundColor: `${t.service.color}15` }}>
                      {t.service.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h5 className="text-sm font-semibold text-foreground">{t.service.label}</h5>
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                          <CheckCircle2 className="h-3 w-3" /> Attivo
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{t.service.idLabel}: <code className="bg-muted px-1 rounded text-foreground">{t.trackingId}</code></p>
                    </div>
                    <a
                      href={t.service.dashboardUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-sky-600 hover:text-sky-700 bg-sky-50 dark:bg-sky-900/20 hover:bg-sky-100 dark:hover:bg-sky-900/40 border border-sky-200 dark:border-sky-800 transition-colors flex-shrink-0"
                    >
                      Apri Dashboard <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  {t.service.bestPractices.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-green-100 dark:border-green-800">
                      <p className="text-[10px] font-medium text-muted-foreground mb-1">Best Practices:</p>
                      <ul className="space-y-0.5">
                        {t.service.bestPractices.map((bp, i) => (
                          <li key={i} className="text-[10px] text-muted-foreground flex items-start gap-1">
                            <span className="text-sky-400 mt-0.5">•</span> {bp}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}

              {TRACKING_SERVICES.filter(s => !activeTracking.find(a => a.serviceId === s.id)).length > 0 && (
                <div className="bg-muted/30 rounded-lg border border-dashed border-border p-3">
                  <p className="text-xs text-muted-foreground mb-2">Servizi non configurati:</p>
                  <div className="flex flex-wrap gap-2">
                    {TRACKING_SERVICES.filter(s => !activeTracking.find(a => a.serviceId === s.id)).map(s => (
                      <span key={s.id} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[10px] bg-muted text-muted-foreground">
                        {s.icon} {s.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-card rounded-lg border border-border p-8 text-center">
              <Shield className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm font-medium text-foreground mb-1">Nessun servizio di tracking configurato</p>
              <p className="text-xs text-muted-foreground mb-4">Configura GA4, GTM, Meta Pixel, LinkedIn Insight o Hotjar dalla sezione Codice.</p>
            </div>
          )}

          {onNavigateToCode && (
            <Button
              variant="outline"
              size="sm"
              onClick={onNavigateToCode}
              className="w-full flex items-center justify-center gap-2 text-sky-600 border-sky-200 hover:bg-sky-50 dark:border-sky-800 dark:hover:bg-sky-900/20"
            >
              <Code className="h-4 w-4" />
              Vai a Codice per configurare i servizi
            </Button>
          )}
        </div>
      )}

      {activeTab === 'webvitals' && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <WebVitalCard
              label="LCP"
              fullLabel="Largest Contentful Paint"
              value={asNum(webVitals?.lcp)}
              unit="ms"
              thresholds={[2500, 4000]}
              description="Tempo di caricamento del contenuto principale"
            />
            <WebVitalCard
              label="CLS"
              fullLabel="Cumulative Layout Shift"
              value={asNum(webVitals?.cls)}
              unit=""
              thresholds={[0.1, 0.25]}
              description="Stabilità visiva della pagina"
            />
            <WebVitalCard
              label="INP"
              fullLabel="Interaction to Next Paint"
              value={asNum(webVitals?.inp)}
              unit="ms"
              thresholds={[200, 500]}
              description="Reattività alle interazioni utente"
            />
          </div>

          {webVitals && Boolean(webVitals.lcp || webVitals.cls !== null || webVitals.inp) && (
            <div className="bg-card rounded-lg border border-border p-4">
              <h4 className="text-sm font-semibold text-foreground mb-3">Legenda Soglie</h4>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-muted-foreground">Buono</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-amber-500" />
                  <span className="text-muted-foreground">Da migliorare</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-muted-foreground">Scarso</span>
                </div>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">
                <p>LCP: {'<'} 2500ms (buono), {'<'} 4000ms (da migliorare), {'>'} 4000ms (scarso)</p>
                <p>CLS: {'<'} 0.1 (buono), {'<'} 0.25 (da migliorare), {'>'} 0.25 (scarso)</p>
                <p>INP: {'<'} 200ms (buono), {'<'} 500ms (da migliorare), {'>'} 500ms (scarso)</p>
              </div>
            </div>
          )}

          {(!webVitals || (!webVitals.lcp && webVitals.cls === null && !webVitals.inp)) && (
            <EmptyState message="Nessun dato Web Vitals disponibile. I dati vengono raccolti automaticamente dai visitatori." />
          )}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string | number; sub?: string }) {
  return (
    <div className="bg-card rounded-lg border border-border p-3">
      <div className="flex items-center gap-2 mb-1">{icon}<span className="text-xs text-muted-foreground">{label}</span></div>
      <p className="text-xl font-bold text-foreground">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

function WebVitalCard({ label, fullLabel, value, unit, thresholds, description }: {
  label: string; fullLabel: string; value: number | null; unit: string;
  thresholds: [number, number]; description: string;
}) {
  const displayValue = value !== null && value !== undefined ? `${value}${unit}` : '-';
  const status = value === null || value === undefined ? 'none' :
    value < thresholds[0] ? 'good' : value < thresholds[1] ? 'warning' : 'bad';

  const colors = {
    none: 'border-border bg-muted/50',
    good: 'border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-800',
    warning: 'border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800',
    bad: 'border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800',
  };

  const textColors = {
    none: 'text-muted-foreground',
    good: 'text-green-600 dark:text-green-400',
    warning: 'text-amber-600 dark:text-amber-400',
    bad: 'text-red-600 dark:text-red-400',
  };

  const statusDot = {
    none: 'bg-muted-foreground/40',
    good: 'bg-green-500',
    warning: 'bg-amber-500',
    bad: 'bg-red-500',
  };

  return (
    <div className={`rounded-lg border p-4 ${colors[status]}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-2.5 h-2.5 rounded-full ${statusDot[status]}`} />
          <span className="text-sm font-bold text-foreground">{label}</span>
        </div>
      </div>
      <p className={`text-2xl font-bold ${textColors[status]}`}>{displayValue}</p>
      <p className="text-[10px] text-muted-foreground mt-1">{fullLabel}</p>
      <p className="text-[10px] text-muted-foreground">{description}</p>
    </div>
  );
}

function FunnelStep({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex-1 text-center">
      <div className={`${color} text-white rounded-lg p-3`}>
        <p className="text-lg font-bold">{value}</p>
      </div>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

function ArrowRight() {
  return <ArrowUpRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />;
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
      <BarChart3 className="h-8 w-8 mb-2 opacity-50" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

function ComparisonCard({ label, current, previous, suffix = '' }: { label: string; current: number; previous: number; suffix?: string }) {
  const diff = previous > 0 ? Math.round(((current - previous) / previous) * 100) : (current > 0 ? 100 : 0);
  const isUp = diff > 0;
  const isDown = diff < 0;
  return (
    <div className="bg-muted/50 rounded-lg p-3 text-center">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-lg font-bold text-foreground">{current}{suffix}</p>
      <div className="flex items-center justify-center gap-1 mt-1">
        {isUp && <ArrowUpRight className="h-3 w-3 text-green-500" />}
        {isDown && <ArrowDownRight className="h-3 w-3 text-red-500" />}
        <span className={`text-[10px] font-medium ${isUp ? 'text-green-600' : isDown ? 'text-red-600' : 'text-muted-foreground'}`}>
          {diff > 0 ? '+' : ''}{diff}%
        </span>
        <span className="text-[10px] text-muted-foreground">vs {previous}{suffix}</span>
      </div>
    </div>
  );
}

function formatEventType(type: string): string {
  const map: Record<string, string> = {
    session_start: 'Sessioni',
    session_end: 'Fine Sessione',
    scroll_depth: 'Scroll',
    time_on_page: 'Tempo Pagina',
    cta_click: 'Click CTA',
    form_click: 'Click Form',
    chatbot_click: 'Chatbot',
    whatsapp_click: 'WhatsApp',
    sms_click: 'SMS',
    web_vital_lcp: 'LCP',
    web_vital_cls: 'CLS',
    web_vital_inp: 'INP',
  };
  return map[type] || type;
}
